import React, { useMemo, useState, useRef } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import camera from 'static/camera.svg';
import { Container } from '~/components/layout';
import { LMIProofDocsUploadContext } from '~/screens/lmi-proof-docs-upload';
import { toBase64 } from '~/utils/files';
import { LmiDocumentTypeSelect } from '../dropdowns/lmi-document-type/lmi-document-type-select.component';
import { UploadFileArrow } from '../svgs';
import { ImagePreview } from './image-preview';
import type { ImagePreviewValidationStatus } from './image-preview/image-preview.component';
import { ImageReviewDialog } from './image-review-dialog';
import {
  DropzoneContainer,
  UploadButton,
  UploadButtonIcon,
  DropzoneDescription,
  FormatDescription,
  UploadButtonCopy,
  UploadButtonCopyIRA,
} from './image-type-uploader.styles';
import { Dropzone } from './image-uploader.style';
import { PdfReviewDialog } from './pdf-review-dialog/pdf-review-dialog.component';

export interface ImageUploaderProps {
  acceptFileTypes: { [key: string]: string[] };
  copy: { documentType: string };
  uploadedImages: any[];
  maxImages: number;
  pageType: 'numbered' | 'front-back' | 'multiple';
  pending: boolean;
  validationStatuses?: Record<string, ImagePreviewValidationStatus>;
  onUploadSuccess: (type: string, file: File, base64Image: string) => void;
  onUploadFailure: (type: string, file: FileRejection) => void;
  onDelete: (id: string) => void;
  context: LMIProofDocsUploadContext;
}

type ImageFile = {
  file: File;
  base64Image: string;
};

const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

interface UploaderProps {
  getRootProps: () => any;
  getInputProps: () => any;
  isDragActive: boolean;
  selectedType: string;
  uploadNextPageMessage: () => string;
  handleDropzoneClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const MobileDropzoneUploader = ({
  getRootProps,
  getInputProps,
  selectedType,
  handleDropzoneClick,
  fileInputRef,
  handleFileInputChange,
}: UploaderProps) => (
  <Dropzone {...getRootProps()} onClick={handleDropzoneClick}>
    <input {...getInputProps()} style={{ display: 'none' }} />

    <input
      ref={fileInputRef}
      data-testid="dropzone-mobile"
      type="file"
      accept="image/*,application/pdf"
      onChange={handleFileInputChange}
      disabled={selectedType === ''}
      style={{ display: 'none' }}
    />

    <Container pt={1}>
      <UploadButtonIcon>
        <UploadFileArrow />
      </UploadButtonIcon>
      <UploadButtonCopy>Upload from your device or take a photo</UploadButtonCopy>
    </Container>
  </Dropzone>
);

const DesktopDropzoneUploader = ({
  getRootProps,
  getInputProps,
  isDragActive,
  selectedType,
  uploadNextPageMessage,
}: UploaderProps) => {
  const rootProps = selectedType ? getRootProps() : {};
  return (
    <Dropzone {...rootProps}>
      <input data-testid="dropzone" {...getInputProps()} disabled={selectedType === ''} />
      <Container pt={1}>
        <img src={camera} alt="Camera" />
      </Container>
      <b>{isDragActive ? 'Drop to upload' : uploadNextPageMessage()}</b>
    </Dropzone>
  );
};

const MobileIraUploader = ({
  getRootProps,
  getInputProps,
  selectedType,
  handleDropzoneClick,
  fileInputRef,
  handleFileInputChange,
}: UploaderProps) => (
  <DropzoneContainer {...getRootProps()} onClick={handleDropzoneClick}>
    <input {...getInputProps()} style={{ display: 'none' }} />

    <input
      ref={fileInputRef}
      data-testid="dropzone-mobile"
      type="file"
      accept="image/*,application/pdf"
      onChange={handleFileInputChange}
      disabled={selectedType === ''}
      style={{ display: 'none' }}
    />

    <DropzoneDescription>Upload from your device or take a photo</DropzoneDescription>

    <UploadButton size="small" disabled={selectedType === ''}>
      <UploadButtonIcon>
        <UploadFileArrow />
      </UploadButtonIcon>
      <UploadButtonCopyIRA>Upload file</UploadButtonCopyIRA>
    </UploadButton>

    <FormatDescription>Max size: 5 MB | Formats: PNG, PDF, JPEG, HEIC</FormatDescription>
  </DropzoneContainer>
);

const DesktopIraUploader = ({ getRootProps, getInputProps, selectedType }: UploaderProps) => {
  const rootProps = selectedType ? getRootProps() : {};
  return (
    <DropzoneContainer {...rootProps}>
      <input data-testid="dropzone" {...getInputProps()} disabled={selectedType === ''} />

      <DropzoneDescription>Drag and drop the file or click on button</DropzoneDescription>

      <UploadButton size="small" disabled={selectedType === ''}>
        <UploadButtonIcon>
          <UploadFileArrow />
        </UploadButtonIcon>
        Upload file
      </UploadButton>

      <FormatDescription>Max size: 5 MB | Formats: PNG, PDF, JPEG, HEIC</FormatDescription>
    </DropzoneContainer>
  );
};

export const ImageTypeUploader = ({
  context,
  acceptFileTypes,
  copy,
  uploadedImages,
  maxImages,
  pageType,
  onDelete,
  onUploadFailure,
  onUploadSuccess,
  pending,
  validationStatuses,
}: ImageUploaderProps) => {
  const isMobile = isMobileDevice();
  const [reviewImageFile, setReviewImageFile] = useState<ImageFile | null>(null);
  const [reviewImageFileType, setReviewImageFileType] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const totalSavedFiles = uploadedImages.filter(f => f.uploadId).length;

  const onDrop = async (accepted: File[], rejected: FileRejection[]) => {
    if (accepted.length) {
      const file = accepted[0];
      const base64Image = await toBase64(file);
      setReviewImageFile({ file, base64Image });
      setReviewImageFileType(file.type);
    } else if (rejected.length) {
      const rejectedFile = rejected[0];
      onUploadFailure(selectedType, rejectedFile);
    }
  };

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      const file = files[0];

      const isValidType = Object.values(acceptFileTypes)
        .flat()
        .some(ext => file.name.toLowerCase().endsWith(ext.replace('.', '')));
      const isValidSize = file.size <= 5 * 1000 * 1000;

      if (!isValidType || !isValidSize) {
        onUploadFailure(selectedType, {
          file,
          errors: [
            { code: isValidType ? 'file-too-large' : 'file-invalid-type', message: 'Invalid file' },
          ],
        } as FileRejection);
        return;
      }

      const base64Image = await toBase64(file);
      setReviewImageFile({ file, base64Image });
      setReviewImageFileType(file.type);
    }

    if (event.target) {
      event.target.value = '';
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: acceptFileTypes,
    maxSize: 5 * 1000 * 1000, // 5 MB
    maxFiles: maxImages,
    multiple: false,
    onDrop,
    noClick: isMobile,
    validator: (file: File) => {
      const alreadyUploaded = uploadedImages.map(f => f.file.docName).includes(file.name);
      return alreadyUploaded ? { code: 'already-uploaded', message: 'Already uploaded' } : null;
    },
  });

  const uploadNextPageMessage = () => {
    let nextPageDescription: string;
    if (pageType === 'front-back') {
      nextPageDescription = totalSavedFiles ? 'Back' : 'Front';
    } else if (pageType === 'numbered') {
      nextPageDescription = `Page ${totalSavedFiles + 1}`;
    } else {
      nextPageDescription = '';
    }
    return `Upload ${copy.documentType} ${nextPageDescription && `(${nextPageDescription})`}`;
  };

  const handleDropzoneClick = () => {
    if (isMobile && fileInputRef.current && selectedType) {
      fileInputRef.current.click();
    }
  };

  const uploaderProps = {
    getRootProps,
    getInputProps,
    isDragActive,
    selectedType,
    uploadNextPageMessage,
    handleDropzoneClick,
    fileInputRef,
    handleFileInputChange,
  };

  const lmiDocumentTypeOptions = useMemo(() => {
    const docTypes = {
      card: 'Card',
      letter: 'Letter',
      utilityBill: 'Utility bill',
      accountStatement: 'Portal screenshot',
    };
    return Object.keys(docTypes).map(typeKey => {
      return {
        disabled: false,
        selected: false,
        text: docTypes[typeKey],
        value: typeKey,
      };
    });
  }, []);

  const renderUploader = () => {
    if (context?.hasIra) {
      return isMobile ? (
        <MobileIraUploader {...uploaderProps} />
      ) : (
        <DesktopIraUploader {...uploaderProps} />
      );
    }

    return isMobile ? (
      <MobileDropzoneUploader {...uploaderProps} />
    ) : (
      <DesktopDropzoneUploader {...uploaderProps} />
    );
  };

  return (
    <>
      <Container display="flex" flexDirection="column">
        {uploadedImages.map(({ proofType, file }) => {
          return file ? (
            <React.Fragment key={file.id}>
              <LmiDocumentTypeSelect
                options={lmiDocumentTypeOptions}
                value={proofType}
                disabled={file.error !== undefined || !!proofType}
                onChange={value => setSelectedType(value)}
              />
              <ImagePreview
                errorCode={file.error}
                key={file.id}
                onDelete={() => onDelete(file.id)}
                base64Image={file.base64Image}
                disableDeletion={pending}
                uploaded={file.error === undefined && !!file.id}
                isPdf={file.type === 'application/pdf'}
                validationStatus={validationStatuses?.[file.id]}
              />
            </React.Fragment>
          ) : null;
        })}
      </Container>

      {!pending && (
        <>
          <LmiDocumentTypeSelect
            options={lmiDocumentTypeOptions}
            value={selectedType}
            disabled={pending}
            onChange={value => setSelectedType(value)}
          />

          {renderUploader()}
        </>
      )}

      {!!reviewImageFile &&
        (reviewImageFileType === 'application/pdf' ? (
          <PdfReviewDialog
            base64Pdf={reviewImageFile.base64Image}
            onApprove={base64Pdf => {
              onUploadSuccess(selectedType, reviewImageFile.file, base64Pdf);
              setReviewImageFile(null);
              setSelectedType('');
            }}
            onCancel={() => setReviewImageFile(null)}
          />
        ) : (
          <ImageReviewDialog
            base64Image={reviewImageFile.base64Image}
            fileType={reviewImageFile.file.type}
            onApprove={base64Image => {
              onUploadSuccess(selectedType, reviewImageFile.file, base64Image);
              setReviewImageFile(null);
              setSelectedType('');
            }}
            onCancel={() => setReviewImageFile(null)}
          />
        ))}
    </>
  );
};
